import { useEffect } from 'react';

/**
 * Helper function to sanitize title strings and prevent HTML injection
 * Converts < and > to their HTML entities to prevent any HTML/script injection
 * 
 * @param {string} text - The text to sanitize
 * @returns {string} Sanitized text
 */
const sanitizeTitle = (text) => {
  if (!text) return '';
  
  // Convert to string if not already
  const str = String(text);
  
  // Replace angle brackets with their HTML entities
  return str
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

/**
 * Custom hook to set the page title with consistent formatting
 * 
 * @param {string|string[]} title - The title or parts of the title to set
 * @param {boolean} includeAppName - Whether to include the app name at the end (default: true)
 */
const usePageTitle = (title, includeAppName = true) => {
  useEffect(() => {
    const appName = 'TradingCard.Life';
    
    // If on the homepage, just use the app name
    if (!title || (Array.isArray(title) && title.length === 0)) {
      document.title = appName;
      return;
    }
    
    // Convert title to string if it's an array of parts
    let formattedTitle;
    if (Array.isArray(title)) {
      // Filter out any empty or null segments and sanitize each part
      const validParts = title
        .filter(part => part)
        .map(part => sanitizeTitle(part));
      formattedTitle = validParts.join(' - ');
    } else {
      formattedTitle = sanitizeTitle(title);
    }
    
    // Set the document title with or without the app name
    document.title = includeAppName 
      ? `${formattedTitle} - ${appName}` 
      : formattedTitle;
      
  }, [title, includeAppName]);
};

export default usePageTitle;